import { useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useCookies } from "react-cookie";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ReactQueryProvider, getQueryClient } from "@/dataProvider/reactQuery";
import { RetrievalProgressProvider } from "@/dataProvider/retrievals";
import { Toaster } from "@/components";
import { ProtectedRoute, PublicRoute, RequireCapability } from "@/routes/guards";
import { AppLayout } from "@/components/layouts/AppLayout";
import { generateRouteConfigs } from "@/routes/generator";
import { ApolloProvider } from "@apollo/client/react";
import { apollo } from "@/lib/apollo";
import { ErrorBoundary } from "@/components";
import { useFetchUserData } from "@/hooks/auth/useFetchUserData";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { useShallow } from "zustand/react/shallow";
import { RetrievalProgressRefreshManager } from "@/components/providers/RetrievalProgressRefreshManager";
import { RetrievalProgressInitialManager } from "@/components/providers/RetrievalProgressInitialManager";
import { AgentRunResumeManager } from "@/components/providers/AgentRunResumeManager";
import { PlaybookRunResumeManager } from "@/components/providers/PlaybookRunResumeManager";
import { withOrgScope } from "@/routes/orgScopedRoute";

/**
 * Auth Handler - Detects cookie and triggers user data fetch + navigation
 * Must be inside Router context
 */
function AuthHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies] = useCookies(["auth_token"]);
  const token = cookies.auth_token;
  
  // Store actions
  const { setToken, setUser, setLoadingUser } = useAuthStoreV1(
    useShallow((s) => ({ setToken: s.setToken, setUser: s.setUser, setLoadingUser: s.setLoadingUser }))
  );
  
  // Track if we've already handled initial navigation after login
  const hasNavigatedRef = useRef(false);
  
  // ✅ CRITICAL: Set token in store BEFORE making API calls
  // Axios interceptor needs this to add Authorization header
  useEffect(() => {
    if (token) {
      setToken(token);
    }
  }, [token, setToken]);
  
  // Fetch user data when token is available
  const { data, isSuccess, isError } = useFetchUserData(token);

  // Clear the initial loading flag once the first fetch settles, one way —
  // never set back to true, so a later background refetch (e.g. org switch)
  // doesn't re-trigger nav/org-selector loading skeletons.
  useEffect(() => {
    if (isSuccess || isError) {
      setLoadingUser(false);
    }
  }, [isSuccess, isError, setLoadingUser]);

  // Handle data after successful fetch - save user and navigate ONLY from public routes
  useEffect(() => {
    if (isSuccess) {
      const user = data?.user;
      if (!user) {
        console.warn("User data response did not include a user; staying on current route.");
        return;
      }

      // Always hydrate the store with user data
      setUser(user);
      
      // Define public/auth routes where we should redirect after login
      const publicRoutes = ["/", "/auth/login", "/auth/callback"];
      const currentPath = location.pathname;
      const isOnPublicRoute = 
        publicRoutes.some(route => currentPath === route) || 
        currentPath.includes("secret_key");
      
      // Only navigate if:
      // 1. We're on a public route (coming from login/callback)
      // 2. We haven't navigated yet (prevent loops)
      if (isOnPublicRoute && !hasNavigatedRef.current) {
        hasNavigatedRef.current = true;
        
        // Check if any organization has incomplete retrieval
        const hasIncompleteRetrieval = user.organizations?.some(
          (org: any) => !org.metadataTypeCounts || org.metadataTypeCounts.length === 0
        );
        
        // Navigate based on org setup status
        if (hasIncompleteRetrieval) {
          console.log("📦 New org detected, redirecting to metadata page");
          navigate("/metadata", { replace: true });
        } else {
          console.log("✅ All metadata retrieved, redirecting to dashboard");
          navigate("/app/dashboard", { replace: true });
        }
      } else if (!isOnPublicRoute) {
        // We're on a protected route (like /org/connection after refresh)
        console.log("✅ User data loaded, staying on current page:", currentPath);
      }
    }
  }, [isSuccess, data, navigate, setUser, location.pathname]);
  
  return null; // This component doesn't render anything
}

function AppContent() {
  const routeConfigs = generateRouteConfigs();

  return (
    <>
      {/* Headless SSE managers — outside Router so connections survive route changes */}
      <RetrievalProgressRefreshManager />
      <RetrievalProgressInitialManager />
      <AgentRunResumeManager />
      <PlaybookRunResumeManager />
      <Router>
        {/* Auth Handler - watches for cookie and triggers navigation */}
        <AuthHandler />
        <AppLayout>
        <Routes>
          {routeConfigs.map((config, index) => {
            // --- Handle redirects ---
            if (config.isRedirect && config.redirectTo) {
              return (
                <Route
                  key={`${config.path}-${index}`}
                  path={config.path}
                  element={<Navigate to={config.redirectTo} replace />}
                />
              );
            }

            // --- Route metadata flags --
            const isPublic = config.metadata?.protected === false;
            const allowSecretKey =
              config.metadata?.allowSecretKey === true;

            // --- 🧠 Identify if this is the metadata page ---
            const isMetadataPage =
              config.path?.includes("metadata") ||
              config.path?.startsWith("/metadata");

            // --- Route element definition ---
            const routeElement = isPublic ? (
              <PublicRoute redirectTo="/app/dashboard">
                <config.Component />
              </PublicRoute>
            ) : (
              <ProtectedRoute
                redirectTo="/auth/login"
                allowSecretKey={allowSecretKey}
              >
                {/*
                  Capability authorization, INSIDE the auth guard and OUTSIDE the module.
                
                  Inside, because "are you signed in" must be answered before "may you see this" —
                  an unauthenticated visitor belongs at the login screen, not at a page explaining
                  which module their (absent) role lacks.
                
                  Outside the module, because `<config.Component />` here is an element, not a
                  rendered component: if the guard returns something else the module never mounts
                  and never fires a request. Putting the check inside each page would mean the
                  module's queries had already gone out before anyone asked whether they should.
                
                  Applied to every protected route rather than a hand-picked list, so a new gated
                  module inherits the guard from `navConfig` without anyone remembering to opt in.

                  Org scope, for the same reason: every protected page is mounted afresh when the
                  active org changes, so a page's own view state (an open document, a generated
                  query, a selected release) cannot carry one org's data into the next. Inside the
                  capability guard, so an org the user may not see this module in never mounts it.
                  A page opts out with `meta.orgScope: 'keep'` in routes/config.ts, and names the
                  URL params that point at one org's records (`?session=`) in `meta.orgScopedParams`.
                */}
                <RequireCapability>
                  {withOrgScope(config.metadata, <config.Component />)}
                </RequireCapability>
              </ProtectedRoute>
            );

            // --- ✅ Conditional ErrorBoundary application ---
            return (
              <Route
                key={`${config.path}-${index}`}
                path={config.path === "*" ? "*" : config.path}
                element={
                  isMetadataPage ? (
                    // 🚀 SKIP ErrorBoundary for MetadataExplorer
                    routeElement
                  ) : (
                    // 🛡️ Apply ErrorBoundary for all other routes
                    <ErrorBoundary>{routeElement}</ErrorBoundary>
                  )
                }
              />
            );
          })}
        </Routes>
        </AppLayout>
        <Toaster />
      </Router>
    </>
  );
}

export function App() {
  return (
    <ApolloProvider client={apollo}>
      <ReactQueryProvider queryClient={getQueryClient()}>
        <RetrievalProgressProvider>
          <ThemeProvider>
            <AppContent />
          </ThemeProvider>
        </RetrievalProgressProvider>
      </ReactQueryProvider>
    </ApolloProvider>
  );
}
